import { motion, AnimatePresence } from 'framer-motion';
import { FaTimes, FaDownload, FaExpand } from 'react-icons/fa';
import { useEffect } from 'react';

const PDFPreview = ({ edition, onClose }) => {
  // Prevent body scroll when modal is open
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, []);

  // Close on Escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        />

        {/* Modal */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.9, y: 20 }}
          className="relative w-full max-w-6xl max-h-[90vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col"
        >
          {/* Header */}
          <div className="bg-primary-green text-white px-6 py-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-4xl">{edition.id === 'micro' ? '🌈' : '🚀'}</span>
              <div>
                <h3 className="text-2xl font-display font-bold">
                  {edition.name}
                </h3>
                <p className="text-sm text-white/80">
                  Sample Preview - Ages {edition.ageRange}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {/* Download Button */}
              <motion.a
                href={edition.thumbnail}
                download
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="p-3 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                title="Download PDF"
              >
                <FaDownload className="text-xl" />
              </motion.a>

              {/* Fullscreen Button */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={() => window.open(edition.thumbnail, '_blank')}
                className="p-3 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                title="Open in new tab"
              >
                <FaExpand className="text-xl" />
              </motion.button>

              {/* Close Button */}
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onClick={onClose}
                className="p-3 bg-white/20 hover:bg-white/30 rounded-lg transition-colors"
                title="Close"
              >
                <FaTimes className="text-xl" />
              </motion.button>
            </div>
          </div>

          {/* PDF Viewer */}
          <div className="flex-1 bg-neutral-gray overflow-auto">
            {edition.thumbnail ? (
              <iframe
                src={edition.thumbnail}
                className="w-full h-full min-h-[600px]"
                title={`${edition.name} Sample`}
                frameBorder="0"
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center">
                  <div className="text-8xl mb-4">📄</div>
                  <h3 className="text-2xl font-display font-bold text-primary-green-dark mb-2">
                    PDF Sample Coming Soon!
                  </h3>
                  <p className="text-neutral-gray-dark">
                    We're preparing amazing content for you.
                  </p>
                </div>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="bg-accent-blue/10 px-6 py-4 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="text-center sm:text-left">
              <p className="text-neutral-gray-dark">
                Love what you see? Subscribe to get fresh content every week! 🎉
              </p>
            </div>
            <motion.a
              href="/subscribe"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="btn-primary whitespace-nowrap"
            >
              Subscribe Now 🚀
            </motion.a>
          </div>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default PDFPreview;